import { AdmissionDeniedError, admissionRedirectFor } from './admission-redirect';

type TokenGetter = () => Promise<string>;

let _getToken: TokenGetter | null = null;

const API_BASE = import.meta.env.VITE_API_BASE_URL || '';

/** The full URL for an API path, for callers that cannot go through `fetch` (e.g. upload progress). */
export function apiUrl(url: string): string {
  return url.startsWith('http') ? url : `${API_BASE}${url}`;
}

/** The bearer header for the signed-in user, or none when auth is not configured. */
export async function authHeaders(): Promise<Record<string, string>> {
  if (!_getToken) return {};
  return { Authorization: `Bearer ${await _getToken()}` };
}

export function configureAuth(getToken: TokenGetter) {
  _getToken = getToken;
}

/** Redirects to `/error` when the response is an admission refusal, and throws
 *  so the caller stops. */
async function enforceAdmission(response: Response): Promise<void> {
  if (response.status !== 403 && response.status !== 503) return;
  const body: unknown = await response
    .clone()
    .json()
    .catch(() => null);
  const redirect = admissionRedirectFor(response.status, body, window.location.pathname);
  if (!redirect) return;
  window.location.assign(redirect.href);
  throw new AdmissionDeniedError(redirect.code, redirect.message);
}

export async function apiFetch(url: string, options?: RequestInit): Promise<Response> {
  const authHeader: Record<string, string> = {};

  if (_getToken) {
    const token = await _getToken();
    authHeader['Authorization'] = `Bearer ${token}`;
  }

  // Prepend API base URL for relative paths (e.g., /api/v1/workshops)
  const fullUrl = apiUrl(url);

  const response = await fetch(fullUrl, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...authHeader,
      ...options?.headers,
    },
  });
  await enforceAdmission(response);
  return response;
}
